// Dwellow video interaction.
//
// One embed script that reuses the existing `.video-wrapper` / `.dwellow-video`
// markup and powers two behaviours from the same code:
//
//   1. Inline players  - lazy-load `data-src` on first play, toggle the poster /
//      play button via `.is-playing`, keep only one video playing at a time, and
//      on stop/end restore the poster (the cover image) instead of freezing on
//      the last painted frame.
//
//   2. Modal player    - an Alpine-driven overlay that plays its video on open
//      and, on close, resets the video and restores the cover image.
//
// Inline `.video-wrapper` elements are auto-initialised (no markup change from
// the current embed), or can be declared explicitly with `x-data="dwellowVideo"`.
// The modal is `x-data="dwellowVideoModal"`.

const PLAY_EVENT = 'dwellow-video:play';
const CONTROLLERS = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

function getVideo(scope) {
    return scope && typeof scope.querySelector === 'function' ? scope.querySelector('video') : null;
}

function isFalse(value) {
    return String(value == null ? '' : value).trim().toLowerCase() === 'false';
}

// Body scroll lock, reference-counted so overlapping modals behave.
let scrollLocks = 0;
let savedBodyOverflow = '';
function lockScroll(on) {
    if (typeof document === 'undefined' || !document.body) return;
    if (on) {
        if (scrollLocks === 0) {
            savedBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        scrollLocks += 1;
    } else if (scrollLocks > 0) {
        scrollLocks -= 1;
        if (scrollLocks === 0) document.body.style.overflow = savedBodyOverflow;
    }
}

function isDisplayed(el) {
    if (!el || typeof window === 'undefined') return false;
    const style = window.getComputedStyle(el);
    // Treat opacity:0 as hidden too, so a Webflow fade-close (opacity -> 0 while
    // display stays flex) is detected as "hidden" and an invisible, faded-out
    // control is not treated as focusable.
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
}

function focusables(root) {
    if (!root) return [];
    const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'video[controls]',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.prototype.filter.call(
        root.querySelectorAll(selector),
        el => isDisplayed(el) && el.getAttribute('tabindex') !== '-1'
    );
}

// Framework-free controller for a single <video> inside a `.video-wrapper`.
// Shared by the inline players and the modal so behaviour is identical.
function getController(video, wrapper, options) {
    if (!video) return null;
    if (CONTROLLERS && CONTROLLERS.has(video)) return CONTROLLERS.get(video);

    const opts = options || {};
    const playBtn = wrapper ? wrapper.querySelector('.video-play-btn') : null;
    const setPlaying = on => {
        if (wrapper) wrapper.classList.toggle('is-playing', !!on);
        // While playing, the poster play button is faded out (opacity:0) but
        // would still be a keyboard tab stop / announced control. Take it out of
        // the tab order and the accessibility tree, and restore it on stop.
        if (playBtn) {
            if (on) {
                playBtn.setAttribute('tabindex', '-1');
                playBtn.setAttribute('aria-hidden', 'true');
            } else {
                playBtn.removeAttribute('tabindex');
                playBtn.removeAttribute('aria-hidden');
            }
        }
    };

    const load = () => {
        if (video.getAttribute('src')) return;
        const src = video.getAttribute('data-src');
        if (src) {
            video.setAttribute('src', src);
            video.load();
        }
    };

    const play = () => {
        load();
        const result = video.play();
        if (result && typeof result.catch === 'function') {
            // Autoplay / gesture policies can reject; leave the poster and play
            // button visible so the viewer can start it with a direct click.
            result.catch(() => setPlaying(false));
        }
        return result;
    };

    const pause = () => video.pause();

    const reset = () => {
        video.pause();
        // Restore the poster (cover image). Unloading the source returns the
        // element to its initial state so the poster shows again instead of the
        // last painted frame; the next play lazy-loads `data-src` once more.
        if (video.getAttribute('src')) {
            video.removeAttribute('src');
            video.load();
        }
        try { video.currentTime = 0; } catch (_) { /* not seekable yet */ }
        setPlaying(false);
    };

    const onPlay = () => {
        setPlaying(true);
        // Only one Dwellow video plays at a time (inline + modal).
        document.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: { video } }));
    };
    const onPause = () => setPlaying(false);
    const onEnded = () => reset();
    const onOtherPlay = event => {
        if (event.detail && event.detail.video !== video) pause();
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    document.addEventListener(PLAY_EVENT, onOtherPlay);

    // Wire the poster play button + click-to-play, matching the existing embed.
    let onBtnClick = null;
    let onVideoClick = null;
    if (opts.wireControls !== false) {
        if (playBtn) {
            onBtnClick = () => play();
            playBtn.addEventListener('click', onBtnClick);
        }
        onVideoClick = () => { if (video.paused) play(); };
        video.addEventListener('click', onVideoClick);
    }

    const controller = {
        video,
        wrapper,
        play,
        pause,
        reset,
        toggle() { if (video.paused) play(); else pause(); },
        destroy() {
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('ended', onEnded);
            document.removeEventListener(PLAY_EVENT, onOtherPlay);
            if (playBtn && onBtnClick) playBtn.removeEventListener('click', onBtnClick);
            if (onVideoClick) video.removeEventListener('click', onVideoClick);
            if (CONTROLLERS) CONTROLLERS.delete(video);
        }
    };

    if (CONTROLLERS) CONTROLLERS.set(video, controller);
    return controller;
}

// Inline player: optional explicit component. `x-data="dwellowVideo"`.
function inlineComponent() {
    return {
        controller: null,
        init() {
            const wrapper = this.$root;
            if (wrapper) wrapper.dataset.videoInit = '1';
            this.controller = getController(getVideo(wrapper), wrapper);
        },
        play() { if (this.controller) this.controller.play(); },
        pause() { if (this.controller) this.controller.pause(); },
        toggle() { if (this.controller) this.controller.toggle(); },
        reset() { if (this.controller) this.controller.reset(); },
        destroy() { if (this.controller) this.controller.destroy(); }
    };
}

// Modal player. `x-data="dwellowVideoModal"`, `x-show="open"`.
function modalComponent() {
    return {
        open: false,
        id: '',
        autoplay: true,
        controllers: [],
        lastFocus: null,
        hideObserver: null,

        init() {
            const root = this.$root;
            this.id = root.getAttribute('data-video-modal') || '';
            this.autoplay = !isFalse(root.getAttribute('data-autoplay'));

            root.querySelectorAll('.video-wrapper').forEach(wrapper => {
                wrapper.dataset.videoInit = '1';
                const controller = getController(getVideo(wrapper), wrapper);
                if (controller) this.controllers.push(controller);
            });

            // Opt-in resilience for a Webflow-driven modal: reset when it hides.
            if (root.hasAttribute('data-reset-on-hide')) {
                this.watchHide(root);
            }
        },

        show() {
            if (this.open) return;
            this.lastFocus = document.activeElement;
            this.open = true;
            lockScroll(true);
            this.$nextTick(() => {
                const first = focusables(this.$root)[0];
                if (first && typeof first.focus === 'function') first.focus();
                if (this.autoplay && this.controllers[0]) this.controllers[0].play();
            });
        },

        close() {
            if (!this.open) return;
            this.open = false;
            this.controllers.forEach(c => c.reset());
            lockScroll(false);
            if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
                this.lastFocus.focus();
            }
            this.lastFocus = null;
        },

        toggle() { if (this.open) this.close(); else this.show(); },

        // Keep Tab focus inside the open modal.
        trapFocus(event) {
            if (!this.open) return;
            const items = focusables(this.$root);
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            if (event.shiftKey && (active === first || !this.$root.contains(active))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        },

        watchHide(root) {
            if (typeof MutationObserver === 'undefined') return;
            let wasVisible = isDisplayed(root);
            const observer = new MutationObserver(() => {
                const visible = isDisplayed(root);
                if (wasVisible && !visible) {
                    // Webflow drives visibility here and never took our scroll
                    // lock, so only reset the video. Touching the shared
                    // scroll-lock counter would steal another open modal's lock.
                    this.controllers.forEach(c => c.reset());
                }
                wasVisible = visible;
            });
            observer.observe(root, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
            this.hideObserver = observer;
        },

        destroy() {
            this.controllers.forEach(c => c.destroy());
            if (this.hideObserver) this.hideObserver.disconnect();
            if (this.open) lockScroll(false);
        }
    };
}

// Auto-init plain `.video-wrapper` elements so the existing inline embed keeps
// working with no markup change. Skips explicit Alpine components and anything
// a modal owns.
function autoInitInline(scope) {
    const root = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    root.querySelectorAll('.video-wrapper').forEach(wrapper => {
        if (wrapper.dataset.videoInit === '1') return;
        if (wrapper.hasAttribute('x-data')) return;
        if (wrapper.closest('[data-video-modal]')) return;
        wrapper.dataset.videoInit = '1';
        getController(getVideo(wrapper), wrapper);
    });
}

function cssEscape(value) {
    if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
        return window.CSS.escape(value);
    }
    return String(value).replace(/["\\]/g, '\\$&');
}

function findModal(id, near) {
    if (near) {
        const owned = near.closest('[data-video-modal]');
        if (owned) return owned;
    }
    if (id) {
        const byId = document.querySelector(`[data-video-modal="${cssEscape(id)}"]`);
        if (byId) return byId;
    }
    return document.querySelector('[data-video-modal]');
}

function modalData(el) {
    if (!el || !window.Alpine || typeof window.Alpine.$data !== 'function') return null;
    try { return window.Alpine.$data(el); } catch (_) { return null; }
}

// Delegated triggers so any element can open/close a modal with a data
// attribute, no per-trigger Alpine required:
//   <a data-video-modal-open="hero">Watch</a>
//   <button data-video-modal-close>Close</button>
function setupDelegatedTriggers() {
    if (typeof document === 'undefined' || document.__dwellowVideoTriggers) return;
    document.__dwellowVideoTriggers = true;
    document.addEventListener('click', event => {
        const opener = event.target.closest && event.target.closest('[data-video-modal-open]');
        if (opener) {
            const modal = findModal(opener.getAttribute('data-video-modal-open'), null);
            const data = modalData(modal);
            if (data && typeof data.show === 'function') {
                event.preventDefault();
                data.show();
            }
            return;
        }
        const closer = event.target.closest && event.target.closest('[data-video-modal-close]');
        if (closer) {
            const modal = findModal(closer.getAttribute('data-video-modal-close'), closer);
            const data = modalData(modal);
            if (data && typeof data.close === 'function') {
                event.preventDefault();
                data.close();
            }
        }
    });
}

export function registerDwellowVideo() {
    if (typeof document === 'undefined') return;

    setupDelegatedTriggers();

    const initTree = () => {
        // Only the plain (non-Alpine) inline wrappers need manual init. Alpine
        // initialises every x-data element itself — the modals and any explicit
        // dwellowVideo players, including ones injected after start (its own
        // mutation observer handles those). Calling Alpine.initTree here would
        // double-initialise modals present at load, because this runs during
        // alpine:init, before Alpine's own root walk.
        autoInitInline(document);
    };

    const register = () => {
        if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
        window.Alpine.data('dwellowVideo', inlineComponent);
        window.Alpine.data('dwellowVideoModal', modalComponent);
        initTree();
    };

    if (window.Alpine) {
        register();
    } else {
        document.addEventListener('alpine:init', register);
    }
    document.addEventListener('DOMContentLoaded', initTree);
}

registerDwellowVideo();
